import { Component, inject, input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { ExpenseFilter } from '../../../../core/filters/expense.filter';
import { ChartOfAccountCategory, ExpenseStatus } from '../../../../core/enums/data.enums';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ParentFocusDirective } from '../../../../core/directives/parent-focus.directive';
import { CurrencyUtilities } from '../../../../core/utilities/currency.utilities';
import { BaseSearchFilterComponent } from '../../../../core/base-classes/base-search-filter.component';
import { ChartOfAccountTypeaheadComponent } from '../../../../parts/chart-of-account-typeahead/chart-of-account-typeahead.component';
import { DatePlaceholderDirective } from '../../../../core/directives/date-placeholder.directive';
import { MultiSelectComponent } from '../../../../parts/multi-select/multi-select.component';

@Component({
  selector: 'ms-expense-search-filter',
  imports: [CommonModule, FormsModule, FaDuotoneIconComponent, ParentFocusDirective, ChartOfAccountTypeaheadComponent, DatePlaceholderDirective, MultiSelectComponent],
  templateUrl: './expense-search-filter.component.html',
  styleUrl: './expense-search-filter.component.scss'
})
export class ExpenseSearchFilterComponent extends BaseSearchFilterComponent<ExpenseFilter> implements OnInit {
  public filter = input.required<ExpenseFilter>();
  public ChartOfAccountCategory = ChartOfAccountCategory;

  public selectStatusOptions = [ExpenseStatus.Pending, ExpenseStatus.Cleared, ExpenseStatus.Voided];
  public selectedStatuses: ExpenseStatus[] = [];

  // Single-value text inputs for vendor / reference (mapped to arrays on submit)
  public vendorInput: string = '';
  public referenceNumberInput: string = '';

  // Amount range in major units (e.g. dollars); converted to minor units on submit.
  public minimumAmountDisplay: number | undefined = undefined;
  public maximumAmountDisplay: number | undefined = undefined;

  private userSessionService = inject(UserSessionService);

  public ngOnInit(): void {
    this.selectedStatuses = [...(this.filter().statuses || [])];
    this.initializeInputsFromFilter();
    this.setFilterCount();
  }

  public override get activeFilter(): ExpenseFilter {
    return this.filter();
  }

  protected override getDefaultFilter(): ExpenseFilter {
    return ListConstants.EXPENSES_FILTER_DEFAULT();
  }

  protected override get preservedFilterKeys(): (keyof ExpenseFilter)[] {
    return ['organizationIds', 'timeZoneId'];
  }

  protected override get dropdownSelector(): string {
    return '#expense-search-filter-dropdown';
  }

  protected override beforeApply(): void {
    const filter = this.filter();
    const minorUnits = this.userSessionService.currentMinorUnits();

    const vendor = this.vendorInput?.trim();
    filter.vendorNames = vendor ? [vendor] : [];

    const reference = this.referenceNumberInput?.trim();
    filter.referenceNumbers = reference ? [reference] : [];

    filter.minimumAmount = this.minimumAmountDisplay != undefined
      ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.minimumAmountDisplay), minorUnits)
      : undefined;
    filter.maximumAmount = this.maximumAmountDisplay != undefined
      ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.maximumAmountDisplay), minorUnits)
      : undefined;
  }

  protected override syncStateFromFilter(): void {
    this.selectedStatuses = [...(this.activeFilter.statuses || [])];
    this.initializeInputsFromFilter();
  }

  public onStatusChange(statuses: ExpenseStatus[]): void {
    this.filter().statuses = statuses;
  }

  private initializeInputsFromFilter(): void {
    const filter = this.filter();
    const minorUnits = this.userSessionService.currentMinorUnits();
    this.vendorInput = filter.vendorNames?.[0] ?? '';
    this.referenceNumberInput = filter.referenceNumbers?.[0] ?? '';
    this.minimumAmountDisplay = filter.minimumAmount != null
      ? CurrencyUtilities.convertMinorUnitsToMajor(filter.minimumAmount, minorUnits)
      : undefined;
    this.maximumAmountDisplay = filter.maximumAmount != null
      ? CurrencyUtilities.convertMinorUnitsToMajor(filter.maximumAmount, minorUnits)
      : undefined;
  }

  public override setFilterCount(): void {
    const filter = this.filter();
    this.filterCount =
      (filter.vendorNames?.length || 0) +
      (filter.referenceNumbers?.length || 0) +
      (filter.expenseAccount != null ? 1 : 0) +
      (filter.minimumAmount != null ? 1 : 0) +
      (filter.maximumAmount != null ? 1 : 0) +
      (filter.minimumDatePaid ? 1 : 0) +
      (filter.maximumDatePaid ? 1 : 0) +
      (filter.statuses?.length || 0);
  }
}
